import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Award, FileText, Lock, Pencil, Trash2, TriangleAlert } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { StaffNote, StaffNoteType } from '../../../types/staffManager';
import { MarkdownRenderer } from '../../MarkdownRenderer';
import { CreatorProfile } from '../../CreatorProfile';
import { cn } from '../../../utils/cn';

interface StaffNoteCardProps {
  note: StaffNote;
  canWrite: boolean;
  onEdit?: (note: StaffNote) => void;
  onDelete?: (note: StaffNote) => void;
  /** Deep-link highlight (`?highlight=`). */
  highlighted?: boolean;
  /** Show who the note is about (feed view). */
  showSubject?: boolean;
  compact?: boolean;
}

export const NOTE_TYPE_META: Record<StaffNoteType, { label: string; icon: typeof FileText; tint: string; chip: string }> = {
  note: { label: 'Note', icon: FileText, tint: 'bg-blue-500/10 border-blue-500/20', chip: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  warning: { label: 'Warning', icon: TriangleAlert, tint: 'bg-yellow-500/10 border-yellow-500/20', chip: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  commendation: { label: 'Commendation', icon: Award, tint: 'bg-green-500/10 border-green-500/20', chip: 'bg-green-500/20 text-green-300 border-green-500/30' },
};

const STATUS_CHIP: Record<string, string> = {
  active: 'bg-green-500/15 text-green-300 border-green-500/25',
  revoked: 'bg-red-500/15 text-red-300 border-red-500/25',
  expired: 'bg-glass-100/10 text-glass-400 border-glass-200/20',
};

/** One Staff Manager Note: type tint, confidentiality lock, markdown body, author and lifecycle pills. */
export const StaffNoteCard = memo(({ note, canWrite, onEdit, onDelete, highlighted = false, showSubject = false, compact = false }: StaffNoteCardProps) => {
  const meta = NOTE_TYPE_META[note.type] ?? NOTE_TYPE_META.note;
  const Icon = meta.icon;
  const inactive = note.status !== 'active';
  const subjectLink = note.subject_discord_id ? `/staff/${note.subject_discord_id}` : note.subject_uuid ? `/player/${note.subject_uuid}` : null;

  return (
    <article
      id={`staff-note-${note.id}`}
      className={cn(
        'glass-card relative overflow-hidden border transition-shadow',
        meta.tint,
        inactive && 'opacity-70',
        highlighted && 'ring-2 ring-accent-400/60 shadow-[0_0_30px_rgb(var(--accent-t-500-rgb)/0.35)]',
        compact ? 'p-3' : 'p-4',
      )}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" aria-hidden="true" />
      <header className="flex flex-wrap items-center gap-2">
        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border', meta.chip)}>
          <Icon className="w-3.5 h-3.5" aria-hidden="true" />
          {meta.label}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/20" title="Visible only to Staff Manager Notes readers">
          <Lock className="w-2.5 h-2.5" aria-hidden="true" />
          Staff Manager only
        </span>
        {inactive && <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize', STATUS_CHIP[note.status] ?? STATUS_CHIP.expired)}>{note.status}</span>}
        {note.expires_at && !inactive && (
          <span className="text-[11px] text-glass-500" title={format(new Date(note.expires_at), 'd MMM yyyy HH:mm')}>
            expires {formatDistanceToNow(new Date(note.expires_at), { addSuffix: true })}
          </span>
        )}
        {showSubject && (
          <span className="text-xs text-glass-300 inline-flex items-center gap-1">
            about{' '}
            {subjectLink ? (
              <Link to={subjectLink} className="font-semibold text-accent-300 hover:underline">
                {note.subject_display_name ?? note.subject_discord_id ?? note.subject_uuid ?? note.subject_user_id}
              </Link>
            ) : (
              <span className="font-semibold text-white">{note.subject_display_name ?? note.subject_user_id}</span>
            )}
          </span>
        )}
        {canWrite && (onEdit || onDelete) && (
          <span className="ml-auto inline-flex items-center gap-1">
            {onEdit && (
              <button type="button" onClick={() => onEdit(note)} className="p-1.5 rounded-md text-glass-400 hover:text-white hover:bg-glass-200/10 transition-colors touch-manipulation" title="Edit" aria-label="Edit note">
                <Pencil className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={() => onDelete(note)} className="p-1.5 rounded-md text-glass-400 hover:text-red-300 hover:bg-red-500/10 transition-colors touch-manipulation" title="Delete" aria-label="Delete note">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </span>
        )}
      </header>

      <div className={cn('mt-3 rounded-md bg-dark-800/50 text-sm text-glass-200 leading-relaxed', compact ? 'p-2.5' : 'p-3')}>
        <MarkdownRenderer content={note.content} />
      </div>

      <footer className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-400">
        <span className="inline-flex items-center gap-1">
          By <CreatorProfile creatorId={note.creator_uuid ?? note.creator_id} inline />
        </span>
        <span title={format(new Date(note.created_at), 'd MMM yyyy HH:mm')}>{formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}</span>
        {note.updated_at && (
          <span className="text-glass-500" title={format(new Date(note.updated_at), 'd MMM yyyy HH:mm')}>
            · edited {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}
            {note.updated_by ? ` by ${note.updated_by.split('@')[0]}` : ''}
          </span>
        )}
      </footer>
    </article>
  );
});

StaffNoteCard.displayName = 'StaffNoteCard';
